import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import {ReactNode} from 'react-markdown';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {
  assertNever,
  ElementKeyboardEvent,
  ElementMouseEvent,
  InputChangeEvent,
  InputFocusEvent,
} from 'util/Types';

interface EditModeFieldActionButtonProps {
  onCancel: () => void;
  onComplete: () => void | Promise<void>;
  completeDisabled: boolean;
}

export function EditModeFieldActionButtons(props: EditModeFieldActionButtonProps): JSX.Element {
  return (
    // tabIndex added to make these buttons focusable - clarifies parent blur event relatedTarget
    <span tabIndex={-1}>
      <a
        className={classNames(css(styles.cancelButtonLink), 'metadata-action faded')}
        onClick={props.onCancel}
        {...getTestProps({component: 'EditModeFieldActionButtons', element: 'cancel'})}
      >
        <BenchlingIcon
          className={classNames('metadata-action-icon--close', css(styles.singleButton))}
          icon="close"
        />
      </a>
      {props.completeDisabled && (
        <a
          className={classNames(css(styles.completeButtonLink), 'metadata-action faded')}
          onClick={props.onComplete}
          {...getTestProps({component: 'EditModeFieldActionButtons', element: 'complete'})}
        >
          <BenchlingIcon
            className={classNames('metadata-action-icon--complete', css(styles.singleButton))}
            icon="check"
          />
        </a>
      )}
    </span>
  );
}

interface BaseDoubleClickEditableTextFieldProps {
  value: string;
  viewer: ReactNode;
  onCancel?: () => void;
  onComplete: (changedValue: string) => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  onDraftNameChanged: (name: string) => void;
  isError?: boolean;
  placeholderText?: string;
  maxValueLength?: number;
}

export enum EmptyStringCompletionPolicy {
  CANNOT_COMPLETE,
  REPLACE_WITH_DEFAULT_ON_COMPLETE,
  ALLOW_COMPLETE,
}

interface WithAllowEmptyString {
  emptyStringCompletionPolicy:
    | EmptyStringCompletionPolicy.ALLOW_COMPLETE
    | EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE;
  defaultValue: () => string;
}

interface WithoutAllowEmptyString {
  emptyStringCompletionPolicy: EmptyStringCompletionPolicy.CANNOT_COMPLETE;
  defaultValue?: never;
}

type DoubleClickEditableTextFieldProps = BaseDoubleClickEditableTextFieldProps &
  (WithAllowEmptyString | WithoutAllowEmptyString);

/**
 * Provides a form field with cancel and complete buttons to allow editing of the desired "viewer"
 * on double click. Based off of {@link EditableTextField}, {@link BaseEditableField},
 * and {@link FieldActionButtons}
 *
 * Note, blurring the editor view will trigger a "complete" action - only "X" button and "esc" keys
 * will trigger "cancel" action.
 */
export default function DoubleClickEditableTextField(props: DoubleClickEditableTextFieldProps): JSX.Element {
  const [draftValue, setDraftValue] = useState(props.value);
  const {isEditing, setIsEditing} = props;

  function isTextValidForCompletion(value: string, policy: EmptyStringCompletionPolicy): boolean {
    if (value.trim() === '') {
      switch (policy) {
        case EmptyStringCompletionPolicy.CANNOT_COMPLETE: {
          return false;
        }
        case EmptyStringCompletionPolicy.ALLOW_COMPLETE: {
          return true;
        }
        case EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE: {
          return true;
        }
        default: {
          assert(false, `Unexpected completion policy: ${assertNever(policy)}`);
        }
      }
    }
    return true;
  }

  const canComplete = isTextValidForCompletion(draftValue, props.emptyStringCompletionPolicy);

  function handleStartEdit(e: ElementMouseEvent): void {
    e.stopPropagation();
    setDraftValue(props.value);
    setIsEditing(true);
  }

  function handleCancel(): void {
    setDraftValue(props.value);
    props.onCancel?.();
    setIsEditing(false);
  }

  function handleComplete(): void {
    assert(canComplete, 'DoubleClickEditableTextField should have had completable status here.', {
      value: draftValue,
    });

    let newValue = draftValue;
    if (
      props.emptyStringCompletionPolicy === EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE &&
      newValue.trim() === ''
    ) {
      newValue = props.defaultValue();
      setDraftValue(newValue);
    }
    props.onComplete(newValue);
    setTimeout(() => {
      // Defer telling the caller to close the editor. This gives them one render-cycle to update their
      // internal state
      setIsEditing(false);
    }, 0);
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      handleComplete();
    } else if (keyboard.isEscapeKey(e)) {
      handleCancel();
    } else if (keyboard.isADeleteKey(e)) {
      e.stopPropagation();
    }
  }

  function handleBlur(e: InputFocusEvent): void {
    e.stopPropagation();
    e.preventDefault();
    // Focusing on any non-child element should trigger completion
    if (e.relatedTarget == null || !e.currentTarget.contains(e.relatedTarget)) {
      handleComplete();
    }
  }

  const wrappedViewer = (
    <div
      className={css(styles.wrapper)}
      onDoubleClick={handleStartEdit}
      {...getTestProps({component: 'DoubleClickEditableTextField', element: 'root'})}
    >
      {props.viewer}
    </div>
  );

  const editor = (
    <div
      className={css(styles.editor)}
      onBlur={handleBlur}
      {...getTestProps({component: 'DoubleClickEditableTextField', element: 'root'})}
    >
      <FormInput
        className={classNames('u-flex-grow', css(props.isError && styles.editorError))}
        onChange={(event: InputChangeEvent) => {
          setDraftValue(event.target.value);
          props.onDraftNameChanged(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        focusOnMount={true}
        type="text"
        value={draftValue}
        maxLength={props.maxValueLength}
        placeholder={props.placeholderText}
      />
      <EditModeFieldActionButtons
        onCancel={handleCancel}
        onComplete={handleComplete}
        completeDisabled={canComplete}
      />
    </div>
  );

  return isEditing ? editor : wrappedViewer;
}

const styles = StyleSheet.create({
  singleButton: {width: '12px', height: '12px'},
  cancelButtonLink: {marginLeft: '8px', marginRight: '4px', padding: '0px'},
  completeButtonLink: {marginLeft: '4px', padding: '0px'},
  wrapper: {width: '100%', overflow: 'hidden'},
  editor: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  },
  editorError: {
    border: `1px solid ${RED}`,
  },
});
